import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { LoaderCircle, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Alert from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Link, useNavigate } from "react-router-dom";
import authService from "@/services/auth.service";
import { useTranslation } from "react-i18next";

const createFormSchema = (t: (k: string) => string) =>
  yup.object().shape({
    email: yup
      .string()
      .email(t("validation.emailInvalid"))
      .required(t("validation.emailRequired")),
  });

export default function ForgotPassword() {
  const { t } = useTranslation(["auth"]);

  const form = useForm({
    resolver: yupResolver(createFormSchema(t)),
    values: {
      email: "",
    },
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  const submit = async (values: any) => {
    try {
      setSuccess("");
      setError("");
      await authService.forgotPassword({
        email: values.email,
      });
      form.reset();
      setSuccess(t("forgotPassword.success"));
    } catch (e) {
      setError(e?.response?.data?.message || t("errors.generic"));
      const errors = e?.response?.data?.meta?.errors || {};
      Object.keys(errors)?.forEach((field: any) => {
        form.setError(field, {
          message: errors[field],
        });
      });
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 lg:fixed lg:inset-y-0 lg:left-0 bg-primary dark:bg-primaryDark relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10 dark:opacity-[3%]">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to={"/"}>
            <img className="h-7" src="logo_white.png" alt="" />
          </Link>
          {/* Main Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 bg-card/20 px-4 py-2 rounded-md text-sm">
              <Sparkles className="w-4 h-4" />
              {t("forgotPassword.titleShort")}
            </div>

            <h1 className="text-4xl xl:text-4xl font-semibold leading-tight">
              {t("forgotPassword.title")}
            </h1>

            <p className="text-lg text-white/80 leading-relaxed max-w-md">
              {t("forgotPassword.subtitle")}
            </p>

            {/* Feature List */}
            <div className="space-y-4">
              {[
                "Secure email verification",
                "Link expires in 24 hours",
                "Create a strong new password",
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-5 w-5 rounded-md bg-card/20 flex items-center justify-center">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="text-white/90 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom */}
          <div className="flex items-center gap-4">
            <div className="flex -space-x-2">
              {["JD", "AK", "MR", "SL"].map((initials, i) => (
                <div
                  key={i}
                  className="w-9 h-9 rounded-full bg-card/20 backdrop-blur border-2 border-white/30 flex items-center justify-center text-xs font-medium"
                >
                  {initials}
                </div>
              ))}
            </div>
            <div className="text-sm text-white/80">
              <span className="font-semibold text-white">4.9/5</span> from 200+
              reviews
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col bg-backgroud lg:ml-[50%]">
        {/* Header */}
        <header className="p-4 sm:p-6 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 lg:hidden">
            <img className="h-8" src="/logo_iconn.png" alt="" />
            <span className="font-semibold text-foreground">OneSuite</span>
          </Link>
          <div className="flex items-center gap-3 ml-auto">
            <span className="text-muted-foreground hidden sm:block text-sm">
              Remember your password?
            </span>
            <Button
              onClick={() => navigate("/login")}
              size="sm"
              variant="outline"
              className="border-gray-300"
            >
              Sign in
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </header>

        {/* Form Content */}
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div>
            <Card className="border border-border rounded-md">
              <CardHeader className="space-y-1 pb-4">
                <div className="flex items-center justify-center">
                  <img className="h-11" src="/logo_iconn.png" alt="" />
                </div>
                <CardTitle className="text-lg font-semibold text-center text-foreground">
                  {t("auth:forgotPassword.title")}
                </CardTitle>
                <CardDescription className="text-center text-muted-foreground">
                  {t("auth:forgotPassword.subtitle")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <form
                  onSubmit={form.handleSubmit(submit)}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-muted-foreground">
                      {t("forgotPassword.email")}
                    </Label>
                    <Input
                      placeholder={t("forgotPassword.emailPlaceholder")}
                      value={form.watch("email")}
                      onChange={(e) => form.setValue("email", e.target.value)}
                      error={form.formState?.errors["email"]?.message}
                      name="email"
                      className="h-11"
                    />
                  </div>
                  <Button
                    disabled={form.formState.isSubmitting}
                    className="w-full h-11 bg-primary hover:bg-primaryDark text-white"
                    type="submit"
                  >
                    {form.formState.isSubmitting ? (
                      <LoaderCircle className="animate-spin" size={18} />
                    ) : (
                      <>
                        {t("forgotPassword.sendLink")}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                <div className="pt-2">
                  <Link
                    to="/login"
                    className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-muted-foreground"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {t("forgotPassword.backToLogin")}
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <footer className="p-4 sm:p-6 border-t border-border bg-card">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-sm text-muted-foreground">
            <p>
              {t("forgotPassword.footerRights", {
                year: new Date().getFullYear(),
              })}
            </p>
            <div className="flex gap-4">
              <Link to="#" className="hover:text-muted-foreground">
                {t("forgotPassword.terms")}
              </Link>
              <Link to="#" className="hover:text-muted-foreground">
                {t("forgotPassword.privacy")}
              </Link>
              <Link to="#" className="hover:text-muted-foreground">
                {t("forgotPassword.help")}
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
